import React, { useState, useEffect } from 'react';
import { Layout } from '@/components/layout/Layout';
import { useAuth } from '@/contexts/AuthContext';
import { useDateFormat } from '@/hooks/useDateFormat';
import { useLanguage } from '@/contexts/LanguageContext';
import {
    Activity,
    PauseCircle,
    PlayCircle,
    Wallet,
    Server,
    ShieldCheck,
    RefreshCw,
    Send,
    Search,
    ChevronLeft,
    ChevronRight,
    ExternalLink,
    Clock,
    Copy,
    CheckCircle,
    XCircle
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';

export const BlockchainDashboard = () => {
    const { token } = useAuth();
    const { t } = useLanguage();
    const { formatDate, getTimezoneOffset } = useDateFormat();
    const [stats, setStats] = useState(null);
    const [performance, setPerformance] = useState([]);
    const [events, setEvents] = useState({ data: [], pagination: {} });
    const [loading, setLoading] = useState(true);
    const [transferData, setTransferData] = useState({ to: '', amount: '' });
    const [processingTransfer, setProcessingTransfer] = useState(false);
    const [processingPause, setProcessingPause] = useState(false); // Processing pause/unpause
    const [transferResult, setTransferResult] = useState(null); // { success: boolean, txHash: string, explorerUrl: string }



    // Grid Filters
    const [filters, setFilters] = useState({
        page: 1,
        limit: 20,
        tenant: '',
        issuer: '',
        certId: ''
    });

    // Debounce filter changes
    const [debouncedFilters, setDebouncedFilters] = useState(filters);
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedFilters(filters), 500);
        return () => clearTimeout(timer);
    }, [filters]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const headers = { Authorization: `Bearer ${token}` };

            // Build query string for events
            const params = new URLSearchParams({
                page: debouncedFilters.page,
                limit: debouncedFilters.limit,
                tenant: debouncedFilters.tenant,
                issuer: debouncedFilters.issuer,
                certId: debouncedFilters.certId
            });

            const [statsRes, perfRes, eventsRes] = await Promise.all([
                fetch('/api/admin/blockchain/stats', { headers }),
                fetch('/api/admin/blockchain/performance', { headers }),
                fetch(`/api/admin/blockchain/events?${params}`, { headers })
            ]);

            if (statsRes.ok && perfRes.ok && eventsRes.ok) {
                setStats(await statsRes.json());
                setPerformance(await perfRes.json());
                const eventsData = await eventsRes.json();
                setEvents({
                    data: eventsData.events,
                    pagination: eventsData.pagination
                });
            } else {
                toast.error(t('dashboard.error_loading', 'Failed to load blockchain data'));
            }
        } catch (error) {
            console.error(error);
            toast.error(t('dashboard.network_error', 'Network error loading dashboard'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [token, debouncedFilters]); // Re-fetch when filters change

    // Auto-refresh Queue Health every 3 seconds
    useEffect(() => {
        if (!token) return;

        const interval = setInterval(async () => {
            try {
                const headers = { Authorization: `Bearer ${token}` };
                const res = await fetch('/api/admin/blockchain/stats', { headers });

                if (res.ok) {
                    const newStats = await res.json();
                    // Only update stats, don't reload performance or events
                    setStats(newStats);
                }
            } catch (error) {
                // Silent fail for background polling
                console.warn('Queue health polling failed:', error);
            }
        }, 3000); // Poll every 3 seconds

        return () => clearInterval(interval);
    }, [token]);



    const handlePauseToggle = async () => {
        if (!stats) return;
        const action = stats.contract.isPaused ? 'Unpause' : 'Pause';
        const pauseValue = !stats.contract.isPaused;

        console.log('🔄 Pause Toggle:', {
            currentState: stats.contract.isPaused ? 'PAUSED' : 'ACTIVE',
            action: action,
            sendingPauseValue: pauseValue,
            meaning: pauseValue ? 'Will PAUSE contract' : 'Will UNPAUSE contract'
        });

        if (!window.confirm(t(`dashboard.confirm_${action.toLowerCase()}`, `Are you sure you want to ${action} the smart contract?`))) return;

        setProcessingPause(true);

        try {
            const res = await fetch('/api/admin/blockchain/pause', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ pause: pauseValue })
            });

            if (res.ok) {
                const data = await res.json();

                // Update state immediately from backend response
                setStats(prev => ({
                    ...prev,
                    contract: {
                        ...prev.contract,
                        isPaused: data.isPaused
                    }
                }));

                setProcessingPause(false);
                toast.success(
                    `${t(`dashboard.success_${action.toLowerCase()}`, `Contract ${action}d successfully`)} - TX: ${data.txHash.substring(0, 10)}...`
                );

                // Refresh full data to ensure everything is in sync
                fetchData();
            } else {
                setProcessingPause(false);
                toast.error(t('dashboard.action_failed', `Failed to ${action} contract`));
            }
        } catch (error) {
            setProcessingPause(false);
            toast.error(t('dashboard.error_generic', 'Error executing action'));
        }
    };

    const handleTransfer = async (e) => {
        e.preventDefault();
        if (!transferData.to || !transferData.amount) return;
        if (!window.confirm(t('dashboard.confirm_transfer', `Send ${transferData.amount} MATIC to ${transferData.to}?`))) return;

        setProcessingTransfer(true);
        setTransferResult(null); // Clear previous result

        try {
            const res = await fetch('/api/admin/blockchain/transfer', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ toAddress: transferData.to, amount: transferData.amount })
            });

            const data = await res.json();
            if (res.ok) {
                toast.success(t('dashboard.transfer_success', 'Transfer Successful!') + ' TX: ' + data.txHash.substring(0, 10) + '...');
                setTransferData({ to: '', amount: '' });
                setTransferResult({
                    success: true,
                    txHash: data.txHash,
                    explorerUrl: data.explorerUrl || `https://polygonscan.com/tx/${data.txHash}`
                });
                fetchData();
            } else {
                toast.error(data.message || t('dashboard.transfer_failed', 'Transfer failed'));
                setTransferResult({
                    success: false,
                    error: data.message || 'Transfer failed'
                });
            }
        } catch (error) {
            toast.error(t('dashboard.error_generic', 'Error processing transfer'));
            setTransferResult({
                success: false,
                error: error.message || 'Network error'
            });
        } finally {
            setProcessingTransfer(false);
        }
    };

    const handleCopyAddress = (address) => {
        navigator.clipboard.writeText(address);
        toast.success(t('common.copied', 'Address copied to clipboard!'));
    };

    if (loading && !stats) return (
        <Layout>
            <div className="flex items-center justify-center h-screen">
                <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
            </div>
        </Layout>
    );

    return (
        <Layout>
            <div className="p-6 space-y-6">
                <div className="flex justify-between items-center">
                    <h1 className="text-2xl font-bold flex items-center gap-2 text-gray-800">
                        <Activity className="w-8 h-8 text-blue-600" />
                        {t('dashboard.title', 'Blockchain Control Center')}
                    </h1>
                    <button
                        onClick={fetchData}
                        className="p-2 bg-white border border-gray-200 rounded-full hover:bg-gray-50 transition shadow-sm"
                        title={t('common.refresh', 'Refresh')}
                    >
                        <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''} text-gray-600`} />
                    </button>
                </div>

                {/* Top Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {/* Balance Card */}
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                        <div className="flex justify-between items-start">
                            <div>
                                <p className="text-sm text-gray-500">{t('dashboard.relayer_balance', 'Relayer Balance')}</p>
                                <p className="text-2xl font-bold text-gray-800">
                                    {stats?.relayer.balance} <span className="text-sm text-purple-600">MATIC</span>
                                </p>
                            </div>
                            <Wallet className="w-8 h-8 text-purple-100 fill-purple-500" />
                        </div>
                        <div className="mt-2 text-xs text-gray-400 truncate" title={stats?.relayer.address}>
                            {stats?.relayer.address}
                        </div>
                    </div>

                    {/* Owner Card */}
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                        <div className="flex justify-between items-start">
                            <div className="flex-1">
                                <p className="text-sm text-gray-500 mb-1">{t('dashboard.contract_owner', 'Contract Owner')}</p>
                                <div className="flex items-center gap-2">
                                    <p className="text-xs font-mono font-medium text-gray-700 break-all">
                                        {stats?.contract.owner}
                                    </p>
                                    <button
                                        onClick={() => handleCopyAddress(stats?.contract.owner)}
                                        className="p-1 hover:bg-gray-100 rounded transition-colors flex-shrink-0"
                                        title={t('common.copy', 'Copy address')}
                                    >
                                        <Copy className="w-4 h-4 text-gray-500" />
                                    </button>
                                </div>
                            </div>
                            <ShieldCheck className="w-8 h-8 text-green-100 fill-green-500 flex-shrink-0" />
                        </div>
                        <div className="mt-2 text-xs text-gray-400">
                            Nonce: {stats?.relayer.currentNonce}
                        </div>
                    </div>

                    {/* Status Card */}
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                        <div className="flex justify-between items-start">
                            <div>
                                <p className="text-sm text-gray-500">{t('dashboard.contract_status', 'Contract Status')}</p>
                                <div className={`mt-1 px-2 py-0.5 text-sm font-medium rounded-full w-fit ${stats?.contract.isPaused
                                    ? 'bg-red-100 text-red-700'
                                    : 'bg-green-100 text-green-700'
                                    }`}>
                                    {stats?.contract.isPaused ? t('status.paused', 'PAUSED') : t('status.active', 'ACTIVE')}
                                </div>
                            </div>
                            {stats?.contract.isPaused
                                ? <PauseCircle className="w-8 h-8 text-red-500" />
                                : <PlayCircle className="w-8 h-8 text-green-500" />
                            }
                        </div>
                        <button
                            onClick={handlePauseToggle}
                            disabled={processingPause}
                            className={`mt-3 text-xs w-full py-1 rounded font-medium transition flex items-center justify-center gap-1 ${processingPause
                                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                : 'bg-[#6B9BD1] hover:bg-[#5A8AC0] text-white'
                                }`}
                        >
                            {processingPause ? (
                                <>
                                    <RefreshCw className="w-3 h-3 animate-spin" />
                                    {t('action.processing', 'Processing...')}
                                </>
                            ) : (
                                stats?.contract.isPaused ? t('action.resume', 'Resume Contract') : t('action.pause', 'Pause Contract')
                            )}
                        </button>
                    </div>

                    {/* Queue Health */}
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                        <div className="flex justify-between items-start">
                            <div>
                                <p className="text-sm text-gray-500">{t('dashboard.queue_health', 'Queue Health')}</p>
                                <div className="flex gap-2 mt-1">
                                    <span className="text-xl font-bold text-orange-500">{stats?.queue.pending}</span>
                                    <span className="text-xs text-gray-400 self-end mb-1">{t('status.pending', 'pending')}</span>
                                </div>
                            </div>
                            <Server className="w-8 h-8 text-blue-100 fill-blue-500" />
                        </div>
                        <div className="mt-2 flex gap-2 text-xs">
                            <span className="text-green-600 bg-green-50 px-1.5 rounded">{stats?.queue.mined} {t('status.mined', 'mined')}</span>
                            <span className="text-red-600 bg-red-50 px-1.5 rounded">{stats?.queue.failed} {t('status.failed', 'failed')}</span>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Performance Chart */}
                    <div className="lg:col-span-2 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                        <h3 className="text-lg font-semibold mb-4 text-gray-800">{t('dashboard.rpc_performance', 'RPC Performance (Latency ms)')}</h3>
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={performance}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis dataKey="rpcUrl" tick={{ fontSize: 0 }} />
                                    <YAxis />
                                    <Tooltip
                                        contentStyle={{ backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #eee' }}
                                        labelStyle={{ color: '#666', fontSize: '12px' }}
                                    />
                                    <Legend />
                                    <Bar name={t('chart.avg_latency', "Avg Latency")} dataKey="avgLatencyMs" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                                    <Bar name={t('chart.max_latency', "Max Latency")} dataKey="maxLatencyMs" fill="#93c5fd" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Transfer Panel */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                        <h3 className="text-lg font-semibold mb-4 text-gray-800 flex items-center gap-2">
                            <Send className="w-5 h-5" />
                            {t('dashboard.emergency_transfer', 'Emergency Transfer')}
                        </h3>
                        <form onSubmit={handleTransfer} className="space-y-4">
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">{t('common.to_address', 'To Address')}</label>
                                <input
                                    type="text"
                                    placeholder="0x..."
                                    value={transferData.to}
                                    onChange={e => setTransferData({ ...transferData, to: e.target.value })}
                                    className="w-full text-sm p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">{t('common.amount', 'Amount')} (MATIC)</label>
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    placeholder="0.0"
                                    value={transferData.amount}
                                    onChange={e => {
                                        const value = e.target.value;
                                        // Only allow numbers and single decimal point
                                        if (value === '' || /^\d*\.?\d*$/.test(value)) {
                                            setTransferData({ ...transferData, amount: value });
                                        }
                                    }}
                                    onKeyDown={e => {
                                        // Allow: backspace, delete, tab, escape, enter, decimal point
                                        if ([8, 9, 27, 13, 46, 110, 190].includes(e.keyCode) ||
                                            // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
                                            (e.keyCode === 65 && e.ctrlKey) ||
                                            (e.keyCode === 67 && e.ctrlKey) ||
                                            (e.keyCode === 86 && e.ctrlKey) ||
                                            (e.keyCode === 88 && e.ctrlKey) ||
                                            // Allow: home, end, left, right, down, up
                                            (e.keyCode >= 35 && e.keyCode <= 40)) {
                                            return;
                                        }
                                        // Prevent if not a number
                                        if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
                                            e.preventDefault();
                                        }
                                    }}
                                    className="w-full text-sm p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={processingTransfer || !transferData.to || !transferData.amount}
                                className="w-full py-2 bg-[#6B9BD1] text-white rounded-lg hover:bg-[#5A8AC0] transition disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center gap-2"
                            >
                                {processingTransfer ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                {t('action.send_funds', 'Send Funds')}
                            </button>

                            {/* Transfer Result */}
                            {transferResult && (
                                <div className={`mt-3 p-3 rounded-lg text-xs ${transferResult.success
                                    ? 'bg-green-50 border border-green-200'
                                    : 'bg-red-50 border border-red-200'
                                    }`}>
                                    {transferResult.success ? (
                                        <div className="space-y-1">
                                            <div className="flex items-center gap-1 text-green-700 font-medium">
                                                <CheckCircle className="w-3.5 h-3.5" />
                                                {t('dashboard.transfer_success', 'Transfer Successful!')}
                                            </div>
                                            <div className="text-green-600">
                                                <span className="font-medium">TX:</span>{' '}
                                                <a
                                                    href={transferResult.explorerUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="underline hover:text-green-800 font-mono"
                                                >
                                                    {transferResult.txHash.substring(0, 10)}...{transferResult.txHash.substring(transferResult.txHash.length - 8)}
                                                </a>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-1 text-red-700">
                                            <XCircle className="w-3.5 h-3.5" />
                                            <span className="font-medium">{t('dashboard.transfer_failed', 'Transfer failed')}:</span>
                                            <span>{transferResult.error}</span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </form>

                    </div>
                </div>

                {/* Transaction Grid */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="p-6 border-b border-gray-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                        <h3 className="text-lg font-semibold text-gray-800">{t('dashboard.transaction_history', 'Transaction History')}</h3>

                        <div className="flex gap-3 flex-wrap w-full md:w-auto">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder={t('filter.tenant', "Filter by Tenant")}
                                    className="pl-9 pr-3 py-1.5 text-sm border rounded-lg w-full md:w-40"
                                    value={filters.tenant}
                                    onChange={e => setFilters({ ...filters, page: 1, tenant: e.target.value })}
                                />
                            </div>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder={t('filter.issuer', "Filter by Issuer")}
                                    className="pl-9 pr-3 py-1.5 text-sm border rounded-lg w-full md:w-40"
                                    value={filters.issuer}
                                    onChange={e => setFilters({ ...filters, page: 1, issuer: e.target.value })}
                                />
                            </div>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder={t('filter.cert_id', "Filter by Cert ID")}
                                    className="pl-9 pr-3 py-1.5 text-sm border rounded-lg w-full md:w-40"
                                    value={filters.certId}
                                    onChange={e => setFilters({ ...filters, page: 1, certId: e.target.value })}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left">
                            <thead className="bg-gray-50 text-gray-500 font-medium border-b border-gray-100">
                                <tr>
                                    <th className="px-6 py-3">{t('grid.event', 'Event')}</th>
                                    <th className="px-6 py-3">{t('grid.tx_hash', 'Tx Hash')}</th>
                                    <th className="px-6 py-3">
                                        <div className="flex items-center gap-1">
                                            <Clock className="w-3 h-3" />
                                            {t('grid.timestamp', 'Timestamp')}
                                            <span className="ml-1 px-1.5 py-0.5 bg-blue-50 text-blue-500 rounded font-mono text-[10px] border border-blue-100">{getTimezoneOffset()}</span>
                                        </div>
                                    </th>
                                    <th className="px-6 py-3">{t('grid.tenant', 'Tenant')}</th>
                                    <th className="px-6 py-3">{t('grid.operator', 'Operator')}</th>
                                    <th className="px-6 py-3">{t('grid.cert_id', 'Cert ID')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {events.data.length > 0 ? events.data.map(event => (
                                    <tr key={event.id} className="hover:bg-gray-50 transition">
                                        <td className="px-6 py-3 font-medium text-gray-700">{event.eventName}</td>
                                        <td className="px-6 py-3 font-mono text-xs text-blue-600">
                                            <a
                                                href={`https://polygonscan.com/tx/${event.txHash}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="flex items-center gap-1 hover:underline"
                                            >
                                                {event.txHash.substring(0, 8)}...{event.txHash.substring(60)}
                                                <ExternalLink className="w-3 h-3" />
                                            </a>
                                        </td>
                                        <td className="px-6 py-3 text-gray-500 whitespace-nowrap">
                                            {formatDate(event.timestamp, 'full')}
                                        </td>
                                        <td className="px-6 py-3 text-gray-600 truncate max-w-[150px]" title={event.args.tenant}>
                                            {event.args.tenant}
                                        </td>
                                        <td className="px-6 py-3 text-gray-600 truncate max-w-[150px]" title={event.args.issuer}>
                                            {event.args.issuer}
                                        </td>
                                        <td className="px-6 py-3 text-gray-600 truncate max-w-[150px]" title={event.args.certId}>
                                            {event.args.certId}
                                        </td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-8 text-center text-gray-400">
                                            {t('grid.no_events', 'No events found')}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="p-4 border-t border-gray-100 flex items-center justify-between">
                        <span className="text-xs text-gray-400">
                            {t('pagination.showing', 'Showing')} {events.data.length} {t('pagination.of', 'of')} {events.pagination.total || 0}
                        </span>
                        <div className="flex gap-2">
                            <button
                                disabled={filters.page <= 1}
                                onClick={() => setFilters(prev => ({ ...prev, page: prev.page - 1 }))}
                                className="p-1 px-3 border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </button>
                            <span className="px-2 py-1 text-sm text-gray-600">
                                {t('pagination.page', 'Page')} {filters.page}
                            </span>
                            <button
                                disabled={filters.page >= (events.pagination.totalPages || 1)}
                                onClick={() => setFilters(prev => ({ ...prev, page: prev.page + 1 }))}
                                className="p-1 px-3 border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};
